import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Plus, Trash2 } from 'lucide-react';
import type {
  BuyCatalogResponse,
  CarbonScenarioCohortInputs,
  GridIntensityOptionsResponse,
  SustainabilityConfigRow,
} from '@siliconx/shared';
import {
  carbonScenarioCohortInputsSchema,
  ESG_BUY_NEW_EMBODIED_NOTE,
  ESG_BUY_USED_EMBODIED_CONVENTION,
  esgMoveActionNote,
  resolveDefaultUsageLocation,
  sortGridIntensityOptionsForPicker,
} from '@siliconx/shared';
import { cn } from '@siliconx/shared/ui/utils';
import { api, throwIfNotOk } from '../../lib/api';
import { qk } from '../../lib/queryKeys';
import { InfoTooltip } from './InfoTooltip';

/**
 * SIL-441 (W4 of resources/plans/sustainability-forecasting-spec.md §3/§6) —
 * the scenario wizard's buy + move inputs. SIL-547 (W3 of
 * wayfinder-ux-consistency-spec.md §4) moved this component from a step of
 * its own to a "carbon lens" disclosure on the Compare & decide stage — the
 * WHY below is unchanged, only the placement is.
 *
 * WHY THESE ARE NOT COHORTS ON STAGE 1 ("Define paths")
 * -------------------------------------------------------
 * `buy` and `move` are NOT members of the persisted
 * `financial_scenario_cohort.action` enum (`scenario_action`, migration
 * 681) and are not dollar actions in v1. They ride to the server as
 * REQUEST-SCOPED inputs on `POST /scenarios/:id/compare`
 * (`ScenarioCompareRequest.carbonCohorts`, SIL-436) and affect the carbon
 * lens only. Offering them in step 1's Action dropdown beside
 * hold/sell/itad/refresh would therefore be a lie in two directions at
 * once: the DB would reject the save, and a reader would reasonably expect
 * them in the dollar series. They get their own step, and that step says
 * plainly that they are not saved with the scenario.
 *
 * NO MATH HERE. Every figure this step shows is an input echo (a SKU's
 * published per-unit embodied carbon, a country's grid factor) read
 * straight off the API. The arithmetic lives in the shared carbon kernel
 * (`use-phase/scenario-carbon-kernel.ts`) and is invoked server-side by
 * `computeScenarioCarbonBlock` — this file never derives a projected
 * figure, so the wizard and the results surface cannot drift.
 *
 * COPY IS VERBATIM. The per-condition embodied notes (§7/§8) and the move
 * note (§13) come from `dto/esg-export.ts`'s locked SIL-426 constants —
 * legally vetted strings, never retyped inline here.
 */

// ---------------------------------------------------------------------------
// Draft shapes — local to the wizard, converted to the wire DTO on compute
// ---------------------------------------------------------------------------

export interface CarbonBuyDraft {
  key: string;
  label: string;
  assetClassId: string | null;
  skuId: string | null;
  unitCount: number;
  condition: 'new' | 'used';
  timingMonths: number;
  /** Boavizta trigram; null until picked. */
  targetUsageLocation: string | null;
}

export interface CarbonMoveDraft {
  key: string;
  label: string;
  /** Epic 2 (lots-first-class) added 'lot'/'team' — same single-select scope
   * shape as the persisted-cohort `CohortDraft.scope` in $scenarioId.tsx
   * (this is the duplicate the plan calls out). */
  scope: 'all' | 'class' | 'lot' | 'team';
  assetClassId: string | null;
  /** Epic 2 — set only when `scope === 'lot'`. */
  lotId: string | null;
  /** Epic 2 — set only when `scope === 'team'`. */
  teamId: string | null;
  timingMonths: number;
  targetUsageLocation: string | null;
}

let carbonKeySeq = 0;
const nextCarbonKey = () => `carbon-${++carbonKeySeq}`;

export function newCarbonBuyDraft(patch: Partial<CarbonBuyDraft> = {}): CarbonBuyDraft {
  return {
    key: nextCarbonKey(),
    label: 'Hypothetical purchase',
    assetClassId: null,
    skuId: null,
    unitCount: 1,
    condition: 'used',
    timingMonths: 0,
    targetUsageLocation: null,
    ...patch,
  };
}

export function newCarbonMoveDraft(patch: Partial<CarbonMoveDraft> = {}): CarbonMoveDraft {
  return {
    key: nextCarbonKey(),
    label: 'Relocation',
    scope: 'all',
    assetClassId: null,
    lotId: null,
    teamId: null,
    timingMonths: 0,
    targetUsageLocation: null,
    ...patch,
  };
}

/** A buy row the API can actually compute: SKU and target location both
 * resolved. An incomplete row is a half-filled form, not an error — it is
 * dropped from the request rather than 400ing the whole compute. */
export function isBuyDraftComplete(d: CarbonBuyDraft): boolean {
  return d.skuId !== null && d.targetUsageLocation !== null && d.unitCount >= 1;
}

export function isMoveDraftComplete(d: CarbonMoveDraft): boolean {
  if (d.targetUsageLocation === null) return false;
  if (d.scope === 'class') return d.assetClassId !== null;
  if (d.scope === 'lot') return d.lotId !== null;
  if (d.scope === 'team') return d.teamId !== null;
  return true; // 'all'
}

/**
 * Drafts -> `ScenarioCompareRequest.carbonCohorts`. Returns `undefined`
 * when nothing is complete, so a scenario with no hypotheticals sends no
 * `carbonCohorts` key at all (the field is optional; an empty
 * `{buy:[],move:[]}` and an absent one compute identically, but the absent
 * one keeps the request honest about what the reader actually configured).
 *
 * Built through the wire schema's own `.parse`, not a hand-rolled object
 * cast: `skuId` is a branded uuid and `targetUsageLocation` is
 * `.transform`ed to upper case by that schema, so parsing here is what
 * guarantees the client sends exactly the shape the route validates
 * instead of something that merely resembles it.
 */
export function toCarbonCohortInputs(
  buys: readonly CarbonBuyDraft[],
  moves: readonly CarbonMoveDraft[],
): CarbonScenarioCohortInputs | undefined {
  const buy = buys.filter(isBuyDraftComplete).map((d) => ({
    label: d.label.trim() || 'Hypothetical purchase',
    skuId: d.skuId,
    unitCount: d.unitCount,
    condition: d.condition,
    timingMonths: d.timingMonths,
    targetUsageLocation: d.targetUsageLocation,
  }));
  const move = moves.filter(isMoveDraftComplete).map((d) => ({
    label: d.label.trim() || 'Relocation',
    selector:
      d.scope === 'class' && d.assetClassId
        ? { assetClassId: d.assetClassId }
        : d.scope === 'lot' && d.lotId
          ? { lotId: d.lotId }
          : d.scope === 'team' && d.teamId
            ? { teamId: d.teamId }
            : { all: true },
    timingMonths: d.timingMonths,
    targetUsageLocation: d.targetUsageLocation,
  }));
  if (buy.length === 0 && move.length === 0) return undefined;
  return carbonScenarioCohortInputsSchema.parse({
    ...(buy.length > 0 ? { buy } : {}),
    ...(move.length > 0 ? { move } : {}),
  });
}

// ---------------------------------------------------------------------------
// Data
// ---------------------------------------------------------------------------

async function unwrap<T>(res: { ok: boolean; status: number; json: () => Promise<unknown> }): Promise<T> {
  if (!res.ok) {
    await throwIfNotOk(res as Response);
    throw new Error(`HTTP ${res.status}`);
  }
  return (await res.json()) as T;
}

const INPUT_CLASS = 'rounded-md border border-zinc-200 px-2 py-1.5 text-sm text-zinc-900';
const FIELD_CLASS = 'flex flex-col gap-1 text-xs text-zinc-500';

export interface OwnedCohortRef {
  key: string;
  label: string;
  /** null for a whole-portfolio cohort — the buy shortcut needs a single
   * class to resolve "the SKU behind this cohort", so those are skipped. */
  assetClassId: string | null;
}

export interface AssetClassRef {
  id: string;
  name: string;
}

/** Epic 2 (lots-first-class) — minimal shapes for the Move section's
 * "By lot"/"By team" scope selects. */
export interface LotRef {
  id: string;
  name: string;
}
export interface TeamRef {
  id: string;
  name: string;
}

export function ScenarioBuyMoveStep({
  buys,
  moves,
  ownedCohorts,
  assetClasses,
  lots,
  teams,
  horizonMonths,
  onChangeBuys,
  onChangeMoves,
}: {
  buys: CarbonBuyDraft[];
  moves: CarbonMoveDraft[];
  /** Persisted cohorts from step 1 — source of the "buy more of this
   * cohort" shortcut. */
  ownedCohorts: OwnedCohortRef[];
  /** The org's asset-class reference list, for the move selector. Unlike
   * the buy picker's classes (which are gated on published embodied-carbon
   * data) a move needs no footprint at all — it relocates hardware whose
   * rate is already measured — so every class is a legitimate move scope. */
  assetClasses: AssetClassRef[];
  /** Epic 2 — org-scoped lot/team lists for the Move section's scope selects
   * (same posture as `assetClasses` above — a move needs no footprint, so
   * every lot/team is a legitimate scope). */
  lots: LotRef[];
  teams: TeamRef[];
  horizonMonths: number;
  onChangeBuys: (next: CarbonBuyDraft[]) => void;
  onChangeMoves: (next: CarbonMoveDraft[]) => void;
}) {
  const catalogQ = useQuery<BuyCatalogResponse>({
    queryKey: qk.sustainability.buyCatalog(),
    queryFn: async () => unwrap<BuyCatalogResponse>(await api.api.client.sustainability['buy-catalog'].$get({ query: {} })),
  });
  const gridQ = useQuery<GridIntensityOptionsResponse>({
    queryKey: qk.sustainability.gridIntensity(),
    queryFn: async () =>
      unwrap<GridIntensityOptionsResponse>(await api.api.client.sustainability['grid-intensity'].$get()),
  });
  const configQ = useQuery<SustainabilityConfigRow>({
    queryKey: qk.sustainability.config(),
    queryFn: async () => unwrap<SustainabilityConfigRow>(await api.api.client.sustainability.config.$get()),
  });

  const catalog = catalogQ.data;
  /**
   * The world-average WOR row is filtered out of BOTH pickers, not just
   * move's. Move rejects it by spec (§3 "moves to 'No location' disallowed",
   * enforced server-side by `assertMoveTargetLocationAllowed`); for a buy,
   * `carbonBuyCohortInputSchema`'s own docstring states the same rule
   * applies to a buy's target. Offering an option one of the two paths
   * refuses would be an invitation to a 400.
   */
  const locations = useMemo(
    () =>
      // USA-first happens HERE, once, on the single list both pickers render —
      // sorting inside LocationSelect (or in each section) would be two copies
      // of one rule.
      sortGridIntensityOptionsForPicker(
        (gridQ.data?.options ?? []).filter((o) => !o.isNoLocationFloor),
      ),
    [gridQ.data],
  );

  /**
   * What a newly added row starts on. Resolved from the LOADED options, so
   * while the query is in flight this is `null` and a row created in that
   * window keeps today's unselected behaviour rather than being handed a
   * selection the reader never made. Existing rows are never touched — the
   * value is read only at row-construction time, never in an effect that
   * could reach back into session state.
   */
  const defaultLocation = resolveDefaultUsageLocation(locations);

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <p className="text-sm text-zinc-500">
          Model hardware you do not own yet, and relocations of hardware you do. Both feed the
          carbon forecast only — the dollar comparison is unchanged in this version.
        </p>
        {/* The single most surprising property of this step, said before the
            reader invests any typing in it. */}
        <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-2 text-xs text-amber-800">
          <AlertTriangle className="mt-0.5 size-3.5 shrink-0 text-amber-600" />
          <span>
            Purchases and relocations are not saved with the scenario — they apply to the
            comparison you compute in this session. Everything on the other steps is saved as
            usual.
          </span>
        </div>
        {configQ.data && (
          <p className="text-xs text-zinc-500">
            Electricity at any location below is tagged{' '}
            <span className="font-medium text-zinc-700">
              {configQ.data.operated
                ? 'Scope 2 (your organization operates its facilities)'
                : 'Category 8 (your organization is colocated)'}
            </span>
            . Set in Financials setup — it applies to every cohort in the organization, not per
            location.
          </p>
        )}
      </div>

      <BuySection
        buys={buys}
        catalog={catalog}
        catalogLoading={catalogQ.isLoading}
        locations={locations}
        locationsLoading={gridQ.isLoading}
        defaultLocation={defaultLocation}
        ownedCohorts={ownedCohorts}
        horizonMonths={horizonMonths}
        onChange={onChangeBuys}
      />

      <MoveSection
        moves={moves}
        assetClasses={assetClasses}
        lots={lots}
        teams={teams}
        locations={locations}
        locationsLoading={gridQ.isLoading}
        defaultLocation={defaultLocation}
        horizonMonths={horizonMonths}
        onChange={onChangeMoves}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Buy
// ---------------------------------------------------------------------------

type GridOption = GridIntensityOptionsResponse['options'][number];

function BuySection({
  buys,
  catalog,
  catalogLoading,
  locations,
  locationsLoading,
  defaultLocation,
  ownedCohorts,
  horizonMonths,
  onChange,
}: {
  buys: CarbonBuyDraft[];
  catalog: BuyCatalogResponse | undefined;
  catalogLoading: boolean;
  locations: GridOption[];
  locationsLoading: boolean;
  /** Pre-selected target location for a row created from here — `null` while
   * the options are loading or when no USA row exists. */
  defaultLocation: string | null;
  ownedCohorts: OwnedCohortRef[];
  horizonMonths: number;
  onChange: (next: CarbonBuyDraft[]) => void;
}) {
  const update = (key: string, patch: Partial<CarbonBuyDraft>) =>
    onChange(buys.map((b) => (b.key === key ? { ...b, ...patch } : b)));
  const remove = (key: string) => onChange(buys.filter((b) => b.key !== key));

  /**
   * "Buy more of this cohort" — pre-selects the SKU behind an owned cohort.
   * The catalog is sorted most-held-first per class, so the first SKU in
   * the cohort's class IS the SKU that cohort predominantly runs. A cohort
   * whose class the picker cannot offer (no published footprint, or
   * transfer-policy excluded) gets no shortcut rather than a shortcut that
   * silently resolves to some other class's hardware.
   */
  const shortcuts = useMemo(() => {
    if (!catalog) return [];
    return ownedCohorts
      .filter((c) => c.assetClassId !== null)
      .map((c) => {
        const sku = catalog.skus.find((s) => s.assetClassId === c.assetClassId);
        return sku ? { cohort: c, sku } : null;
      })
      .filter((x): x is { cohort: OwnedCohortRef; sku: BuyCatalogResponse['skus'][number] } => x !== null);
  }, [catalog, ownedCohorts]);

  return (
    <section className="space-y-3">
      <div>
        <h3 className="text-sm font-medium text-zinc-900">Buy</h3>
        <p className="mt-0.5 text-xs text-zinc-500">
          A hypothetical purchase starts drawing power in its timing month and lands its embodied
          carbon there too. Classes below are whichever ones currently have published
          embodied-carbon data.
        </p>
      </div>

      {catalog && catalog.excludedClasses.length > 0 && (
        <ul className="space-y-1 rounded-md border border-zinc-200 bg-zinc-50 px-3 py-2 text-xs text-zinc-600">
          {catalog.excludedClasses.map((ex) => (
            <li key={ex.assetClassId}>
              <span className="font-medium text-zinc-800">{ex.assetClassName}</span> — {ex.note}
            </li>
          ))}
        </ul>
      )}

      {shortcuts.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-zinc-500">Buy more of:</span>
          {shortcuts.map(({ cohort, sku }) => (
            <button
              key={cohort.key}
              type="button"
              onClick={() =>
                onChange([
                  ...buys,
                  newCarbonBuyDraft({
                    label: `More ${cohort.label}`,
                    assetClassId: sku.assetClassId,
                    skuId: sku.skuId,
                    targetUsageLocation: defaultLocation,
                  }),
                ])
              }
              className="rounded-full border border-brand-200 bg-brand-50 px-2.5 py-1 text-xs font-medium text-brand-700 hover:bg-brand-100"
            >
              {cohort.label}
              <span className="ml-1 font-normal text-brand-600/80">({sku.displayName})</span>
            </button>
          ))}
        </div>
      )}

      {buys.length === 0 && (
        <div className="rounded-md border border-dashed border-zinc-300 p-6 text-center text-sm text-zinc-500">
          No hypothetical purchases yet.
        </div>
      )}

      <div className="space-y-3">
        {buys.map((b) => {
          const classSkus = (catalog?.skus ?? []).filter((s) => s.assetClassId === b.assetClassId);
          const selectedSku = catalog?.skus.find((s) => s.skuId === b.skuId) ?? null;
          return (
            <div key={b.key} className="rounded-lg border border-zinc-200 bg-white p-4">
              <div className="flex items-start gap-3">
                <div className="grid flex-1 grid-cols-2 gap-3 sm:grid-cols-3">
                  <label className={FIELD_CLASS}>
                    Label
                    <input
                      value={b.label}
                      onChange={(e) => update(b.key, { label: e.target.value })}
                      className={INPUT_CLASS}
                    />
                  </label>

                  <label className={FIELD_CLASS}>
                    Asset class
                    <select
                      value={b.assetClassId ?? ''}
                      onChange={(e) =>
                        // Changing class invalidates the SKU — clearing it is
                        // the only safe move; keeping a SKU from the previous
                        // class would send hardware the reader is no longer
                        // looking at.
                        update(b.key, { assetClassId: e.target.value || null, skuId: null })
                      }
                      className={INPUT_CLASS}
                    >
                      <option value="">{catalogLoading ? 'Loading…' : 'Select…'}</option>
                      {(catalog?.classes ?? []).map((ac) => (
                        <option key={ac.assetClassId} value={ac.assetClassId}>
                          {ac.assetClassName}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label className={FIELD_CLASS}>
                    SKU
                    <select
                      value={b.skuId ?? ''}
                      disabled={!b.assetClassId}
                      onChange={(e) => update(b.key, { skuId: e.target.value || null })}
                      className={cn(INPUT_CLASS, 'disabled:bg-zinc-100 disabled:text-zinc-400')}
                    >
                      <option value="">{b.assetClassId ? 'Select…' : 'Pick a class first'}</option>
                      {classSkus.map((s) => (
                        <option key={s.skuId} value={s.skuId}>
                          {s.displayName}
                          {s.ownedUnitCount > 0 ? ` — you own ${s.ownedUnitCount}` : ''}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label className={FIELD_CLASS}>
                    Units
                    <input
                      type="number"
                      min={1}
                      step={1}
                      value={b.unitCount}
                      onChange={(e) =>
                        update(b.key, { unitCount: Math.max(1, Math.floor(Number(e.target.value) || 1)) })
                      }
                      className={INPUT_CLASS}
                    />
                  </label>

                  <label className={FIELD_CLASS}>
                    <span className="inline-flex items-center gap-1">
                      Condition
                      <InfoTooltip label="About new vs used">
                        New and used are modeled under different embodied-carbon conventions, both
                        stated in full below the row.
                      </InfoTooltip>
                    </span>
                    <select
                      value={b.condition}
                      onChange={(e) => update(b.key, { condition: e.target.value as 'new' | 'used' })}
                      className={INPUT_CLASS}
                    >
                      <option value="used">Used</option>
                      <option value="new">New</option>
                    </select>
                  </label>

                  <label className={FIELD_CLASS}>
                    Timing (months, within {horizonMonths})
                    <input
                      type="number"
                      min={0}
                      max={horizonMonths}
                      value={b.timingMonths}
                      onChange={(e) =>
                        update(b.key, {
                          timingMonths: Math.min(horizonMonths, Math.max(0, Number(e.target.value) || 0)),
                        })
                      }
                      className={INPUT_CLASS}
                    />
                  </label>

                  <label className={cn(FIELD_CLASS, 'col-span-2 sm:col-span-3')}>
                    Target location
                    <LocationSelect
                      value={b.targetUsageLocation}
                      loading={locationsLoading}
                      locations={locations}
                      onChange={(v) => update(b.key, { targetUsageLocation: v })}
                    />
                  </label>
                </div>

                <button
                  type="button"
                  aria-label={`Remove ${b.label}`}
                  onClick={() => remove(b.key)}
                  className="mt-5 rounded-md p-1.5 text-zinc-400 hover:bg-red-50 hover:text-red-600"
                >
                  <Trash2 className="size-4" />
                </button>
              </div>

              {selectedSku && b.condition === 'new' && (
                <div className="mt-2 text-xs text-zinc-500">
                  Published embodied carbon:{' '}
                  <span className="font-mono tabular-nums text-zinc-700">
                    {Math.round(selectedSku.embodiedKgco2eqPerUnit).toLocaleString('en-US')} kg CO2e
                  </span>{' '}
                  per unit.
                </div>
              )}

              {/* Per-condition embodied copy, SIL-426 §7/§8 — verbatim, and
                  rendered per row rather than once per section so a screenshot
                  of one purchase carries the convention that purchase was
                  modeled under. */}
              <p className="mt-2 rounded-md bg-zinc-50 px-2.5 py-2 text-xs leading-relaxed text-zinc-600">
                {b.condition === 'new' ? ESG_BUY_NEW_EMBODIED_NOTE : ESG_BUY_USED_EMBODIED_CONVENTION}
              </p>
            </div>
          );
        })}
      </div>

      <button
        type="button"
        onClick={() => onChange([...buys, newCarbonBuyDraft({ targetUsageLocation: defaultLocation })])}
        className="inline-flex items-center gap-1.5 rounded-md border border-dashed border-zinc-300 px-3 py-2 text-sm text-zinc-600 hover:border-zinc-400 hover:text-zinc-800"
      >
        <Plus className="size-4" /> Add purchase
      </button>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Move
// ---------------------------------------------------------------------------

function MoveSection({
  moves,
  assetClasses,
  lots,
  teams,
  locations,
  locationsLoading,
  defaultLocation,
  horizonMonths,
  onChange,
}: {
  moves: CarbonMoveDraft[];
  assetClasses: AssetClassRef[];
  /** Epic 2 (lots-first-class) — see `ScenarioBuyMoveStep`'s own docstring. */
  lots: LotRef[];
  teams: TeamRef[];
  locations: GridOption[];
  locationsLoading: boolean;
  /** Pre-selected target location for a row created from here — `null` while
   * the options are loading or when no USA row exists. */
  defaultLocation: string | null;
  horizonMonths: number;
  onChange: (next: CarbonMoveDraft[]) => void;
}) {
  const update = (key: string, patch: Partial<CarbonMoveDraft>) =>
    onChange(moves.map((m) => (m.key === key ? { ...m, ...patch } : m)));
  const remove = (key: string) => onChange(moves.filter((m) => m.key !== key));

  return (
    <section className="space-y-3 border-t border-zinc-100 pt-6">
      <div>
        <h3 className="text-sm font-medium text-zinc-900">Move</h3>
        <p className="mt-0.5 text-xs text-zinc-500">
          Relocate hardware you already own. &ldquo;No location&rdquo; is not offered as a
          destination — a relocation must resolve a real grid factor.
        </p>
      </div>

      {moves.length === 0 && (
        <div className="rounded-md border border-dashed border-zinc-300 p-6 text-center text-sm text-zinc-500">
          No relocations yet.
        </div>
      )}

      <div className="space-y-3">
        {moves.map((m) => (
          <div key={m.key} className="rounded-lg border border-zinc-200 bg-white p-4">
            <div className="flex items-start gap-3">
              <div className="grid flex-1 grid-cols-2 gap-3 sm:grid-cols-3">
                <label className={FIELD_CLASS}>
                  Label
                  <input
                    value={m.label}
                    onChange={(e) => update(m.key, { label: e.target.value })}
                    className={INPUT_CLASS}
                  />
                </label>

                <label className={FIELD_CLASS}>
                  Scope
                  <select
                    value={m.scope}
                    onChange={(e) => {
                      const scope = e.target.value as 'all' | 'class' | 'lot' | 'team';
                      update(m.key, {
                        scope,
                        assetClassId: scope === 'class' ? (m.assetClassId ?? assetClasses[0]?.id ?? null) : null,
                        lotId: scope === 'lot' ? (m.lotId ?? lots[0]?.id ?? null) : null,
                        teamId: scope === 'team' ? (m.teamId ?? teams[0]?.id ?? null) : null,
                      });
                    }}
                    className={INPUT_CLASS}
                  >
                    <option value="all">Whole portfolio</option>
                    <option value="class">By asset class</option>
                    <option value="lot">By lot</option>
                    <option value="team">By team</option>
                  </select>
                </label>

                {m.scope === 'class' && (
                  <label className={FIELD_CLASS}>
                    Asset class
                    <select
                      value={m.assetClassId ?? ''}
                      onChange={(e) => update(m.key, { assetClassId: e.target.value || null })}
                      className={INPUT_CLASS}
                    >
                      <option value="">Select…</option>
                      {assetClasses.map((ac) => (
                        <option key={ac.id} value={ac.id}>
                          {ac.name}
                        </option>
                      ))}
                    </select>
                  </label>
                )}

                {/* Epic 2 — single-select lot/team per move row, same "one
                    row per lot/team" posture as persisted cohorts. */}
                {m.scope === 'lot' && (
                  <label className={FIELD_CLASS}>
                    Lot
                    <select
                      value={m.lotId ?? ''}
                      onChange={(e) => update(m.key, { lotId: e.target.value || null })}
                      className={INPUT_CLASS}
                    >
                      <option value="">Select…</option>
                      {lots.map((lot) => (
                        <option key={lot.id} value={lot.id}>
                          {lot.name}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
                {m.scope === 'team' && (
                  <label className={FIELD_CLASS}>
                    Team
                    <select
                      value={m.teamId ?? ''}
                      onChange={(e) => update(m.key, { teamId: e.target.value || null })}
                      className={INPUT_CLASS}
                    >
                      <option value="">Select…</option>
                      {teams.map((team) => (
                        <option key={team.id} value={team.id}>
                          {team.name}
                        </option>
                      ))}
                    </select>
                  </label>
                )}

                <label className={FIELD_CLASS}>
                  Timing (months, within {horizonMonths})
                  <input
                    type="number"
                    min={0}
                    max={horizonMonths}
                    value={m.timingMonths}
                    onChange={(e) =>
                      update(m.key, {
                        timingMonths: Math.min(horizonMonths, Math.max(0, Number(e.target.value) || 0)),
                      })
                    }
                    className={INPUT_CLASS}
                  />
                </label>

                <label className={cn(FIELD_CLASS, 'col-span-2 sm:col-span-3')}>
                  Target location
                  <LocationSelect
                    value={m.targetUsageLocation}
                    loading={locationsLoading}
                    locations={locations}
                    onChange={(v) => update(m.key, { targetUsageLocation: v })}
                  />
                </label>
              </div>

              <button
                type="button"
                aria-label={`Remove ${m.label}`}
                onClick={() => remove(m.key)}
                className="mt-5 rounded-md p-1.5 text-zinc-400 hover:bg-red-50 hover:text-red-600"
              >
                <Trash2 className="size-4" />
              </button>
            </div>

            {/* SIL-426 §13, verbatim outside its three slots. `{source}` is
                rendered as "its current location" rather than a trigram: the
                cohort spans however many facilities its assets sit in, and
                the server resolves each one's own factor per class at compute
                time — naming a single source here would be a guess. */}
            <p className="mt-2 rounded-md bg-zinc-50 px-2.5 py-2 text-xs leading-relaxed text-zinc-600">
              {esgMoveActionNote({
                source: 'its current location',
                target: m.targetUsageLocation ?? 'the target location',
                month: m.timingMonths,
              })}
            </p>
          </div>
        ))}
      </div>

      <button
        type="button"
        onClick={() => onChange([...moves, newCarbonMoveDraft({ targetUsageLocation: defaultLocation })])}
        className="inline-flex items-center gap-1.5 rounded-md border border-dashed border-zinc-300 px-3 py-2 text-sm text-zinc-600 hover:border-zinc-400 hover:text-zinc-800"
      >
        <Plus className="size-4" /> Add relocation
      </button>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Shared location picker
// ---------------------------------------------------------------------------

/**
 * Country + g/kWh in the option itself, not in a tooltip: the grid factor
 * is the entire reason one location differs from another in this forecast,
 * so a reader choosing between two of them must be able to see the number
 * while choosing, not after.
 */
function LocationSelect({
  value,
  locations,
  loading,
  onChange,
}: {
  value: string | null;
  locations: GridOption[];
  loading: boolean;
  onChange: (v: string | null) => void;
}) {
  const [showSource, setShowSource] = useState(false);
  const selected = locations.find((o) => o.countryIso3 === value) ?? null;

  return (
    <span className="flex flex-wrap items-center gap-2">
      <select
        aria-label="Target location"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value || null)}
        className={cn(INPUT_CLASS, 'min-w-[16rem]')}
      >
        <option value="">{loading ? 'Loading locations…' : 'Select a location…'}</option>
        {locations.map((o) => (
          <option key={o.countryIso3} value={o.countryIso3}>
            {o.countryIso3} — {o.gCo2ePerKwh.toLocaleString('en-US')} g CO2e/kWh
          </option>
        ))}
      </select>
      {selected && (
        <button
          type="button"
          onClick={() => setShowSource((s) => !s)}
          className="text-xs text-zinc-500 underline decoration-dotted hover:text-zinc-700"
        >
          {showSource ? 'Hide factor source' : 'Factor source'}
        </button>
      )}
      {selected && showSource && (
        <span className="basis-full text-xs text-zinc-500">
          {selected.source} · {selected.sourceVersion} · reference year {selected.referenceYear}
        </span>
      )}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Compare-step companion (SIL-441 × SIL-440)
// ---------------------------------------------------------------------------

/**
 * The results-page echo of this step's hypotheticals — it fills the second
 * column of SIL-440's row-2 grid, beside the quarantined advisory module.
 *
 * WHY IT EXISTS. The buy/move drafts are session-scoped: they are never
 * persisted with the scenario and they appear nowhere in the compare
 * response as a reader-legible list. Without this echo, a comparison whose
 * carbon series was shifted by a hypothetical purchase looks identical to
 * one that was not, and a shared `?stage=3` link (which carries no drafts at
 * all) silently renders a *different* comparison from the one its author
 * saw. Restating the inputs beside the results is what makes that
 * discrepancy visible.
 *
 * ONLY COMPLETE ROWS. It shows exactly the rows `toCarbonCohortInputs`
 * actually sent — a half-filled form is dropped from the request, and
 * listing it here would claim an influence on the numbers that it did not
 * have.
 *
 * NO MATH, same rule as the step itself: every value is an input echo.
 * Renders nothing when no hypothetical fed the comparison — an empty box
 * would read as a surface that failed to populate (the advisory module
 * next to it makes the same call).
 */
export function BuyCohortSummaryModule({
  buys,
  moves,
}: {
  buys: readonly CarbonBuyDraft[];
  moves: readonly CarbonMoveDraft[];
}) {
  const appliedBuys = buys.filter(isBuyDraftComplete);
  const appliedMoves = moves.filter(isMoveDraftComplete);
  if (appliedBuys.length === 0 && appliedMoves.length === 0) return null;

  return (
    <section
      data-testid="buy-cohort-summary-module"
      className="rounded-xl border border-zinc-200 bg-white p-5"
    >
      <div className="mb-3 text-xs font-bold uppercase tracking-wider text-zinc-500">
        Hypotheticals applied to this comparison
      </div>

      <ul className="space-y-2">
        {appliedBuys.map((b) => (
          <li key={b.key} className="rounded-lg border border-zinc-200 bg-zinc-50/70 p-3">
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-[12.5px] font-medium text-zinc-900">
                {b.label.trim() || 'Hypothetical purchase'}
              </span>
              <span className="font-mono text-[13px] tabular-nums text-zinc-500">Buy</span>
            </div>
            <p className="mt-1 text-xs text-zinc-600">
              {b.unitCount} {b.unitCount === 1 ? 'unit' : 'units'} · {b.condition} ·{' '}
              {b.targetUsageLocation} · month {b.timingMonths}
            </p>
          </li>
        ))}
        {appliedMoves.map((m) => (
          <li key={m.key} className="rounded-lg border border-zinc-200 bg-zinc-50/70 p-3">
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-[12.5px] font-medium text-zinc-900">
                {m.label.trim() || 'Relocation'}
              </span>
              <span className="font-mono text-[13px] tabular-nums text-zinc-500">Move</span>
            </div>
            <p className="mt-1 text-xs text-zinc-600">
              {m.scope === 'all'
                ? 'All assets'
                : m.scope === 'lot'
                  ? 'Selected lot'
                  : m.scope === 'team'
                    ? 'Selected team'
                    : 'Selected class'}{' '}
              → {m.targetUsageLocation} · month {m.timingMonths}
            </p>
          </li>
        ))}
      </ul>

      <p className="mt-4 border-t border-zinc-200 pt-3 text-xs leading-relaxed text-zinc-500">
        Session-scoped: these are not saved with the scenario and are not included in a shared link.
        They affect the carbon lens only — the dollar series is unchanged.
      </p>
    </section>
  );
}
